// Printable (A4 landscape) Stock Overview: who holds how many pieces of each
// product, its value, and what they owe. Same look as the Partner Accounts
// Summary print (letterhead, headline cards, navy/gold table). Kept
// self-contained so the identical file can live in both erp-thekseoul and rxk.
const NAVY = '#1B2A4A';
const GOLD = '#AE8A4C';
const INK = '#26324A';
const MUTED = '#6B7280';
const HAIRLINE = '#E7E9EE';
const SOFT_BG = '#F6F7FB';
const GOLD_WASH = '#FBF6EC';
const RED = '#B42318';

export interface StockDocBranding {
  issuer_name: string | null;
  // e.g. "a Brand of Sol Grace Construction BD. Ltd." (invoice_templates.concern_line)
  concern_line: string | null;
  issuer_address: string | null;
  issuer_contact: string | null;
  issuer_email: string | null;
  logo_url: string | null;
}

export interface StockDocColumn {
  sku: string;
  name: string;
}

export interface StockDocRow {
  id: string;
  name: string;
  type: string;
  // Pieces of each product still with the partner, keyed by sku.
  inHand: Record<string, number>;
  units: number;
  value: number;
  dueNow: number;
  totalDue: number;
}

export interface StockDocTotals {
  perColumn: number[];
  units: number;
  value: number;
  dueNow: number;
  outstanding: number;
}

const money = (v: number) => `৳${(Math.round(v * 100) / 100).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
// "Rx.K Booster 50ml" -> "Booster": short enough for a narrow column.
const shortLabel = (name: string) => name.replace(/^Rx\.?K\s+/i, '').replace(/\s+\d+\s*ml$/i, '').trim() || name;

type Align = 'left' | 'right' | 'center';
const th = (align: Align = 'right') => ({
  padding: '7px 6px',
  fontSize: '8.5px',
  fontWeight: 700,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.06em',
  color: NAVY,
  textAlign: align,
  whiteSpace: 'nowrap' as const,
});
const td = (align: Align = 'right') => ({ padding: '6px 6px', fontSize: '10px', textAlign: align, fontVariantNumeric: 'tabular-nums' as const, whiteSpace: 'nowrap' as const });
const PCS_COL = { width: '64px' };

function Kpi({ label, value, sub, color = NAVY }: { label: string; value: string; sub: string; color?: string }) {
  return (
    <div style={{ flex: 1, border: `1px solid ${HAIRLINE}`, borderTop: `3px solid ${color}`, borderRadius: '8px', padding: '8px 10px', background: '#fff' }}>
      <p style={{ margin: 0, fontSize: '8px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: MUTED }}>{label}</p>
      <p style={{ margin: '3px 0 0', fontSize: '16px', fontWeight: 700, color, fontVariantNumeric: 'tabular-nums' }}>{value}</p>
      <p style={{ margin: '1px 0 0', fontSize: '8.5px', color: MUTED }}>{sub}</p>
    </div>
  );
}

export function StockOverviewDocument({
  branding,
  columns,
  rows,
  totals,
  generatedAt,
  filterLabel,
  headingFont = "'Fraunces', Georgia, serif",
}: {
  branding: StockDocBranding;
  columns: StockDocColumn[];
  rows: StockDocRow[];
  totals: StockDocTotals;
  generatedAt: Date;
  filterLabel: string;
  headingFont?: string;
}) {
  const brandName = branding.issuer_name?.trim() || 'The.K Seoul';
  const concern = branding.concern_line?.trim();
  const contact = [branding.issuer_contact ? `Hotline: ${branding.issuer_contact}` : null, branding.issuer_email].filter(Boolean).join('  ·  ');
  const withStock = rows.filter((r) => r.units > 0).length;
  return (
    <div
      className="stock-report-doc bg-white mx-auto"
      style={{ width: '297mm', minHeight: '180mm', padding: '10mm 12mm', boxSizing: 'border-box', fontFamily: "'Inter', system-ui, sans-serif", color: INK }}
    >
      {/* Letterhead */}
      <div className="flex items-center justify-between gap-8">
        {branding.logo_url ? (
          <img src={branding.logo_url} alt={brandName} crossOrigin="anonymous" style={{ height: '48px', maxWidth: '200px', objectFit: 'contain' }} />
        ) : (
          <h1 className="m-0" style={{ fontFamily: headingFont, fontWeight: 600, fontSize: '22px', color: NAVY }}>
            {brandName}
          </h1>
        )}
        <div className="text-right" style={{ fontSize: '9.5px', color: MUTED, lineHeight: 1.55 }}>
          <p className="m-0" style={{ fontWeight: 700, color: NAVY, fontSize: '10px' }}>
            {concern ? `${brandName} is ${concern}` : brandName}
          </p>
          {branding.issuer_address && <p className="m-0">{branding.issuer_address}</p>}
          {contact && <p className="m-0">{contact}</p>}
        </div>
      </div>
      <div style={{ borderTop: `1px solid ${GOLD}`, marginTop: '8px' }} />
      <div style={{ borderTop: `2px solid ${NAVY}`, marginTop: '2px' }} />

      <div style={{ margin: '10px 0' }}>
        <p className="m-0" style={{ fontFamily: headingFont, fontWeight: 600, fontSize: '18px', color: NAVY }}>
          Stock Overview
        </p>
        <p className="m-0" style={{ fontSize: '9.5px', color: MUTED }}>
          {filterLabel} · {rows.length} partner(s), {withStock} holding stock · Generated{' '}
          {generatedAt.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
        </p>
      </div>

      {/* Headline totals */}
      <div className="flex gap-2" style={{ marginBottom: '10px' }}>
        <Kpi label="Units On Hand" value={totals.units.toLocaleString()} sub="Pieces with all partners" />
        <Kpi label="Value On Hand" value={money(totals.value)} sub="At partner price" color="#3D5A8A" />
        <Kpi label="Due Now (on sold)" value={money(totals.dueNow)} sub="Collect now" color={RED} />
        <Kpi label="Total Due" value={money(totals.outstanding)} sub="If all stock is sold" color={GOLD} />
      </div>

      <table className="w-full stock-overview-table" style={{ borderCollapse: 'collapse' }}>
        {/* Print-only spacer row: <thead> repeats on every printed page, so
            this gives pages 2, 3, … a gap above the column header. */}
        <thead style={{ display: 'table-header-group' }}>
          <tr className="hidden print:table-row" aria-hidden="true">
            <td colSpan={6 + columns.length} style={{ height: '10mm', padding: 0, border: 0 }} />
          </tr>
          {columns.length > 0 && (
            <tr style={{ background: SOFT_BG }}>
              <th colSpan={2} />
              <th colSpan={columns.length} style={{ ...th('center'), borderBottom: `1px solid ${HAIRLINE}`, padding: '5px 6px 3px' }}>
                In hand (pcs)
              </th>
              <th colSpan={4} />
            </tr>
          )}
          <tr style={{ background: SOFT_BG, borderBottom: `2px solid ${NAVY}` }}>
            <th style={{ ...th(), width: '28px' }}>Sr.</th>
            <th style={th('left')}>Partner</th>
            {columns.map((c) => (
              <th key={c.sku} style={{ ...th('center'), ...PCS_COL, color: GOLD, letterSpacing: '0.02em' }}>
                {shortLabel(c.name)}
              </th>
            ))}
            <th style={th()}>Total Pcs</th>
            <th style={th()}>Value</th>
            <th style={th()}>Due Now</th>
            <th style={th()}>Total Due</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id} style={{ borderBottom: `1px solid ${HAIRLINE}`, background: i % 2 === 1 ? SOFT_BG : 'transparent' }}>
              <td style={{ ...td(), color: MUTED }}>{i + 1}</td>
              <td style={{ ...td('left'), whiteSpace: 'normal', minWidth: '170px', lineHeight: 1.25 }}>
                <span style={{ fontWeight: 600, display: 'block' }}>{r.name}</span>
                <span style={{ color: MUTED, fontSize: '8px', textTransform: 'capitalize' }}>{r.type}</span>
              </td>
              {columns.map((c) => {
                const q = r.inHand[c.sku] ?? 0;
                return (
                  <td key={c.sku} style={{ ...td('center'), ...PCS_COL, fontWeight: q ? 700 : 400, color: q ? INK : '#C3C8D2' }}>
                    {q}
                  </td>
                );
              })}
              <td style={{ ...td(), fontWeight: 700 }}>{r.units}</td>
              <td style={td()}>{money(r.value)}</td>
              <td style={{ ...td(), fontWeight: 700, color: r.dueNow > 0 ? RED : MUTED }}>{money(Math.max(0, r.dueNow))}</td>
              <td style={{ ...td(), fontWeight: 700, color: NAVY }}>{money(Math.max(0, r.totalDue))}</td>
            </tr>
          ))}
          <tr style={{ background: GOLD_WASH, borderTop: `2.5px solid ${NAVY}` }}>
            <td style={td()} />
            <td style={{ ...td('left'), fontWeight: 700, color: NAVY, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Total</td>
            {columns.map((c, i) => (
              <td key={c.sku} style={{ ...td('center'), ...PCS_COL, fontWeight: 700 }}>
                {totals.perColumn[i] ?? 0}
              </td>
            ))}
            <td style={{ ...td(), fontWeight: 700 }}>{totals.units.toLocaleString()}</td>
            <td style={{ ...td(), fontWeight: 700 }}>{money(totals.value)}</td>
            <td style={{ ...td(), fontWeight: 700, color: RED }}>{money(totals.dueNow)}</td>
            <td style={{ ...td(), fontWeight: 700, color: NAVY, fontSize: '11px' }}>{money(totals.outstanding)}</td>
          </tr>
        </tbody>
      </table>

      <p style={{ margin: '10px 0 0', fontSize: '8.5px', color: MUTED, lineHeight: 1.6 }}>
        <b>In hand</b>: pieces of each product still with the partner (gift boxes not included). <b>Value</b>: those pieces at partner price.{' '}
        <b>Due Now</b>: value of units reported sold minus payments and offers — collect this now. <b>Total Due</b>: given − returned − paid, i.e. owed once
        everything is sold.
      </p>
    </div>
  );
}
